import React, { useCallback, useMemo, useId } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import FieldLabel from './FieldLabel';
import { ILabel } from '../constants/interfaces';
import {
  getBaseTextFieldStyles,
  getAccessibilityProps,
  getFormHelperTextSlotProps,
  getFieldName,
  getComputedFieldValues,
  inputLimits,
} from '../styles/fieldStyles';

/**
 * Props for the PercentageField component
 */
export interface IPercentageFieldProps {
  /** Current percentage value as string */
  value: string;
  /** Callback fired when the percentage value changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Whether the field is read-only/display only */
  displayOnly?: boolean;
  /** Width of the percentage input */
  width?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Number of decimal places allowed */
  decimalPlaces?: number;
  /** Minimum allowed percentage value */
  min?: number;
  /** Maximum allowed percentage value */
  max?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Percentage symbol to display */
  percentageSymbol?: string;
  /** Callback fired when input gains focus */
  onFocus?: () => void;
  /** Callback fired when input loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for percentage input handling
 */
const percentageUtils = {
  /**
   * Converts decimal to percentage (0.5 -> 50)
   */
  decimalToPercentage: (decimal: number): number => {
    return decimal * 100;
  },

  /**
   * Formats percentage value for display
   */
  formatPercentage: (value: string, decimalPlaces = 4): string => {
    if (!value || isNaN(Number(value))) {
      return value;
    }

    const num = Number(value);
    return num.toLocaleString('en-US', {
      maximumFractionDigits: decimalPlaces,
      minimumFractionDigits: 0,
    });
  },

  /**
   * Checks if percentage value is within bounds
   */
  isWithinBounds: (value: string, min?: number, max?: number): boolean => {
    if (!value) return true;

    const num = Number(value);
    if (isNaN(num)) return false;

    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;

    return true;
  },

  /**
   * Converts percentage to decimal (50 -> 0.5)
   */
  percentageToDecimal: (percentage: number): number => {
    return percentage / 100;
  },

  /**
   * Sanitizes percentage input to allow only numbers and decimal point
   */
  sanitizePercentageInput: (value: string, decimalPlaces = 4): string => {
    // Remove all characters except digits and '.'
    let sanitized = value.replace(/[^0-9.]/g, '');

    // Only allow one decimal point
    const firstDot = sanitized.indexOf('.');
    if (firstDot !== -1) {
      // Remove any additional dots
      sanitized =
        sanitized.substring(0, firstDot + 1) +
        sanitized.substring(firstDot + 1).replace(/\./g, '');
    }

    // Limit decimal places if there's a dot
    if (sanitized.includes('.')) {
      const [intPart, decPart] = sanitized.split('.');
      sanitized =
        intPart + '.' + (decPart ? decPart.slice(0, decimalPlaces) : '');
    }

    // Prevent leading zeros unless immediately followed by a dot
    if (/^0[0-9]+/.test(sanitized)) {
      sanitized = sanitized.replace(/^0+/, '0');
    }

    return sanitized;
  },

  /**
   * Validates percentage value based on field requirements
   */
  validatePercentage: (
    value: string,
    field: ILabel,
    min?: number,
    max?: number
  ): string | null => {
    if (field.isMandatory && !value.trim()) {
      return `${field.text} is required`;
    }

    if (value && isNaN(Number(value))) {
      return `${field.text} must be a valid percentage`;
    }

    const numValue = Number(value);
    if (min !== undefined && numValue < min) {
      return `${field.text} must be at least ${min}%`;
    }

    if (max !== undefined && numValue > max) {
      return `${field.text} must be at most ${max}%`;
    }

    return null;
  },
};

/**
 * PercentageField Component
 *
 * A reusable percentage input component that integrates with form validation
 * and provides a consistent interface for percentage value input across the application.
 *
 * Features:
 * - Configurable decimal places (default 4)
 * - Min/max percentage validation
 * - Automatic input sanitization
 * - Percentage symbol display
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Mobile-optimized decimal keyboards
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <PercentageField
 *   value={interestRate}
 *   onChange={handleRateChange}
 *   field={rateField}
 *   errorText={validationError}
 *   min={0}
 *   max={100}
 *   decimalPlaces={2}
 * />
 * ```
 */
function PercentageField(props: IPercentageFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    displayOnly = false,
    width,
    disabled = false,
    readOnly = false,
    placeholder,
    decimalPlaces = 4,
    min = 0,
    max = 100,
    maxLength = inputLimits.maxLength.percentage,
    percentageSymbol = '%',
    onFocus,
    onBlur,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Memoize computed values using centralized utility plus specific values
  const computedValues = useMemo(
    () => ({
      ...getComputedFieldValues(
        errorText,
        disabled,
        displayOnly,
        width,
        maxLength
      ),
      isWithinBounds: percentageUtils.isWithinBounds(value, min, max),
    }),
    [errorText, disabled, displayOnly, width, maxLength, value, min, max]
  );

  // Optimized change handler with useCallback
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // Allow empty string
      if (newValue === '') {
        onChange('');
        return;
      }

      const sanitizedValue = percentageUtils.sanitizePercentageInput(
        newValue,
        decimalPlaces
      );
      onChange(sanitizedValue);
    },
    [onChange, decimalPlaces]
  );

  // Optimized focus handler
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Optimized blur handler
  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  return (
    <>
      {/* Label with proper accessibility attributes */}
      <FieldLabel field={field} htmlFor={fieldId} isHidden={isLabelHidden} />

      {/* Percentage input with enhanced props */}
      <TextField
        id={fieldId}
        fullWidth={String(computedValues.fieldWidth) === '100%'}
        name={getFieldName(field)}
        error={computedValues.hasError}
        size="small"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={computedValues.isDisabled}
        helperText={computedValues.helperText}
        type="text"
        placeholder={placeholder}
        sx={getBaseTextFieldStyles(computedValues.fieldWidth)}
        inputProps={{
          inputMode: 'decimal',
          max: max,
          maxLength: computedValues.effectiveMaxLength,
          min: min,
          step: 'any',
          ...getAccessibilityProps(
            fieldId,
            labelId,
            errorId,
            field,
            isLabelHidden,
            computedValues.hasError
          ),
          readOnly: readOnly,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{percentageSymbol}</InputAdornment>
          ),
        }}
        slotProps={getFormHelperTextSlotProps(computedValues.hasError, errorId)}
      />
    </>
  );
}

export default PercentageField;

// Export utilities for testing and external use
export { percentageUtils };
